import { animate, motion, useInView, useReducedMotion, type Variants } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

export const EASE = [0.16, 1, 0.3, 1] as const;

const container: Variants = {
  hidden: {},
  shown: { transition: { staggerChildren: 0.03 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 4 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE } },
};

/** Quick page-load fade, lightly staggered. Children opt in by wrapping in <Reveal.Item>. */
export function Reveal({ children, className, as = 'div' }: { children: ReactNode; className?: string; as?: 'div' | 'section' | 'ul' }) {
  const reduce = useReducedMotion();
  const Comp = motion[as];
  return (
    <Comp className={className} variants={container} initial={reduce ? 'shown' : 'hidden'} animate="shown">
      {children}
    </Comp>
  );
}

Reveal.Item = function RevealItem({ children, className, as = 'div', style }: { children: ReactNode; className?: string; as?: 'div' | 'li' | 'section' | 'header'; style?: React.CSSProperties }) {
  const Comp = motion[as];
  return (
    <Comp className={className} variants={item} style={style}>
      {children}
    </Comp>
  );
};

/** Animated number for headline figures. */
export function CountUp({ value, format = (n) => Math.round(n).toLocaleString('id-ID'), duration = 0.6 }: { value: number; format?: (n: number) => string; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? value : 0);
  const from = useRef(0);
  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setShown(value);
      return;
    }
    const controls = animate(from.current, value, { duration, ease: EASE, onUpdate: (v) => setShown(v) });
    from.current = value;
    return () => controls.stop();
  }, [value, inView, reduce, duration]);
  return (
    <span ref={ref} className="num">
      {format(shown)}
    </span>
  );
}

export function PageTransition({ children, id }: { children: ReactNode; id: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div key={id} initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2, ease: EASE }}>
      {children}
    </motion.div>
  );
}
